import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { shortAddress } from "@tenergy/web-core/format";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminAlert, AdminRole } from "../api/types.js";
import { MutationForm } from "../forms/mutation-form.js";
import { utc } from "../format.js";
import {
  Button,
  ErrorNote,
  Panel,
  Pill,
  ScreenHeader,
  Spinner,
  StatRow,
  Table,
  Td,
  Th,
  Tile,
} from "../ui.js";

/**
 * The alert feed, and who has looked at it.
 *
 * NOT AN ALERTS TABLE. `GET /v1/admin/alerts` assembles the feed on every read from what the
 * database already knows: orders that failed in the last 24 h, fund wallets whose chain state
 * has drifted, and providers the cascade has marked unhealthy. An entry disappears when the
 * condition behind it goes away.
 *
 * AN ACKNOWLEDGEMENT SILENCES NOTHING. It is an `editor` action with a mandatory reason
 * (`POST /v1/admin/alerts/ack`), stored as an audit row, and it says "somebody has looked at
 * this" for 24 hours. The button is not rendered for a viewer; a viewer sees who acknowledged.
 */
export function AlertsScreen({ role }: { role: AdminRole }) {
  const alerts = useResource((signal) => {
    void signal;
    return adminReads.alerts({ limit: 100 });
  }, []);
  const [acking, setAcking] = useState<AdminAlert | null>(null);

  const rows = alerts.data?.data ?? [];
  const sorted = [...rows].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  const loaded = alerts.data != null;
  const open = rows.filter((row) => (row.acknowledged ?? null) === null);

  return (
    <>
      <ScreenHeader
        title="Alerts"
        lead="Assembled on every read from failed orders, drifting fund wallets and unhealthy providers. The window for failures is the last 24 hours."
      />

      <StatRow>
        <Tile
          icon="bell"
          label="In the feed"
          value={loaded ? String(rows.length) : null}
          note={loaded ? `as of ${utc(alerts.data?.as_of)}` : "loading…"}
        />
        <Tile
          icon="shield"
          label="Critical"
          tone={rows.some((row) => row.severity === "critical") ? "danger" : "neutral"}
          value={loaded ? String(rows.filter((row) => row.severity === "critical").length) : null}
          note="A drifting or quarantined fund wallet."
        />
        <Tile
          icon="clock"
          label="Not acknowledged"
          tone={open.length > 0 ? "warning" : "neutral"}
          value={loaded ? String(open.length) : null}
          note="Nobody has marked these as seen in the last 24 hours."
        />
        <Tile
          icon="user"
          label="Acknowledged"
          value={loaded ? String(rows.length - open.length) : null}
          note="Seen by an editor, with a reason, inside 24 hours."
        />
      </StatRow>

      {acking === null ? null : (
        <div class="mb-3">
          <Panel>
            <AcknowledgeForm
              alert={acking}
              onDone={() => {
                setAcking(null);
                alerts.reload();
              }}
              onCancel={() => setAcking(null)}
            />
          </Panel>
        </div>
      )}

      <Panel title="Feed" subtitle={loaded ? `as of ${utc(alerts.data?.as_of)}` : undefined}>
        {alerts.loading && !loaded ? (
          <Spinner />
        ) : alerts.error !== null ? (
          <ErrorNote error={alerts.error} onRetry={() => alerts.reload()} />
        ) : (
          <Table
            rows={sorted.length}
            empty="Nothing is wrong: no order failed in the last 24 hours, no fund wallet has drifted and every provider is healthy."
            head={
              <tr>
                <Th>At</Th>
                <Th>Level</Th>
                <Th>Kind</Th>
                <Th>Target</Th>
                <Th>Detail</Th>
                <Th>Acknowledged</Th>
              </tr>
            }
          >
            {sorted.map((alert, index) => {
              const ack = alert.acknowledged ?? null;
              return (
                <tr key={alert.key ?? `${alert.kind}-${alert.target}-${index}`}>
                  <Td mono>{utc(alert.at)}</Td>
                  <Td>
                    <Pill value={alert.severity} />
                  </Td>
                  <Td mono>{alert.kind}</Td>
                  <Td mono>{alert.target}</Td>
                  <Td class="max-w-[360px] text-[11.5px]">{alert.detail}</Td>
                  <Td>
                    {ack !== null ? (
                      <span class="text-[11.5px] text-ink-muted" title={ack.reason}>
                        <span class="num text-ink">{ack.by === null ? "admin" : shortAddress(ack.by)}</span>{" "}
                        {utc(ack.at)}
                        <span class="block max-w-[240px] truncate">{ack.reason}</span>
                      </span>
                    ) : role === "editor" ? (
                      <Button tone="ghost" onClick={() => setAcking(alert)}>
                        Acknowledge
                      </Button>
                    ) : (
                      <span class="text-[11.5px] text-warning">not yet</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Panel>
    </>
  );
}

function AcknowledgeForm({
  alert,
  onDone,
  onCancel,
}: {
  alert: AdminAlert;
  onDone(): void;
  onCancel(): void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <MutationForm
      title={`Acknowledge ${alert.kind} on ${alert.target}`}
      description="Marks the alert as seen, with your reason. It changes nothing in the platform; the condition stays until it is fixed."
      changes={[{ label: "acknowledged", before: null, after: "by you, now" }]}
      effect="At once. Shown for 24 hours; if the condition is still there after that, the alert reads unacknowledged again."
      submitLabel="Acknowledge"
      busy={busy}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .acknowledgeAlert({ kind: alert.kind, target: alert.target, reason })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      {null}
    </MutationForm>
  );
}
